import PropTypes from 'prop-types';
import { Box, Typography, LinearProgress } from '@mui/material';
import PageOverlayHeader from 'features/IRWorkstation/components/PageOverlayHeader';
import KPICard from 'features/IRWorkstation/components/KPICard';
import StatusPill from 'features/IRWorkstation/components/StatusPill';
import fakeData from 'features/IRWorkstation/constants/fakeData';

const FundCard = ({ card, onClickFundV }) => (
	<Box
		onClick={card.isFundV ? onClickFundV : undefined}
		sx={{
			backgroundColor: 'background.paper',
			border: 'solid',
			borderColor: card.activeRaise ? 'primary.main' : 'border.light',
			borderWidth: card.activeRaise ? '2px' : '0.5px',
			borderRadius: '12px',
			padding: '12px',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '8px',
			cursor: card.isFundV ? 'pointer' : 'default',
			'&:hover': card.isFundV ? { borderColor: 'primary.main' } : {},
		}}
	>
		<Box>
			<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
				{card.name}
			</Typography>
			<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
				{card.vintage}
			</Typography>
		</Box>
		<StatusPill label={card.pill.label} color={card.pill.color} />
		{/* full-bleed divider above stats */}
		<Box
			sx={{
				width: 'calc(100% + 32px)',
				mx: '-16px',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
			}}
		/>
		<Box sx={{ width: '100%' }}>
			{card.stats.map((s) => (
				<Box
					key={s.label}
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						py: '3px',
					}}
				>
					<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
						{s.label}
					</Typography>
					<Typography
						sx={{
							fontSize: '12px',
							fontWeight: 600,
							color: s.valueColor
								? `status.${s.valueColor}.text`
								: 'text.primary',
						}}
					>
						{s.value}
					</Typography>
				</Box>
			))}
		</Box>
		{card.bar && (
			<LinearProgress
				variant='determinate'
				value={card.bar.percent}
				sx={{
					height: 6,
					width: '100%',
					borderRadius: 2,
					backgroundColor: 'background.subtle',
					'& .MuiLinearProgress-bar': {
						borderRadius: 2,
						backgroundColor:
							card.bar.color === 'green'
								? 'signal.green'
								: card.bar.color === 'amber'
									? 'signal.amber'
									: 'signal.gray',
					},
				}}
			/>
		)}
		<Box sx={{ flex: 1 }} />
		{/* full-width footer */}
		<Box
			sx={{
				width: 'calc(100% + 24px)',
				mx: '-12px',
				mb: '-12px',
				px: '12px',
				py: '8px',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '0 0 12px 12px',
				backgroundColor: 'background.subtle',
			}}
		>
			<Typography
				sx={{
					fontSize: '11px',
					color: card.footerHighlight ? 'primary.main' : 'text.secondary',
					fontWeight: card.footerHighlight ? 600 : 400,
				}}
			>
				{card.footer}
			</Typography>
		</Box>
	</Box>
);

FundCard.propTypes = {
	card: PropTypes.object.isRequired,
	onClickFundV: PropTypes.func,
};

// ── fund detail (rendered conditionally for any active-raise fund) ──
const FundDetail = ({ fund, onOpenDrawer }) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
		{/* kpi strip */}
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(6, 1fr)',
				gap: '10px',
			}}
		>
			{fund.kpis.map((kpi) => (
				<KPICard key={kpi.label} {...kpi} />
			))}
		</Box>

		{/* pipeline table */}
		<Box
			sx={{
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '12px',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					padding: '10px 14px',
					borderBottom: '0.5px solid',
					borderColor: 'border.light',
					display: 'flex',
					alignItems: 'center',
				}}
			>
				<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
					LP pipeline
				</Typography>
				<Box
					component='button'
					sx={{
						fontSize: '10px',
						fontWeight: 600,
						color: 'vdr.text',
						background: 'vdr.bg',
						border: '0.5px solid',
						borderColor: 'vdr.border',
						borderRadius: '5px',
						padding: '1px 5px',
						cursor: 'pointer',
						fontFamily: 'inherit',
						marginLeft: 'auto',
						'&:hover': { backgroundColor: 'vdr.hover' },
					}}
				>
					↔ VDR Status column - open Raise VDR for full detail
				</Box>
				<Typography
					sx={{ marginLeft: '6px', fontSize: '11px', color: 'text.secondary' }}
				>
					Click re-up score pill for breakdown
				</Typography>
			</Box>
			<Box
				component='table'
				sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}
			>
				<Box component='thead'>
					<Box component='tr' sx={{ backgroundColor: 'background.subtle' }}>
						{[
							'LP',
							'Type',
							'Target',
							'Pipeline Status',
							'Re-up Score',
							'↔ VDR Status',
						].map((h) => (
							<Box
								component='th'
								key={h}
								sx={{
									textAlign: 'left',
									padding: '6px 10px',
									fontSize: '12px',
									fontWeight: 700,
									color: h.includes('VDR') ? 'vdr.text' : 'text.secondary',
									letterSpacing: '0.05em',
								}}
							>
								{h}
							</Box>
						))}
					</Box>
				</Box>
				<Box component='tbody'>
					{fund.pipeline.map((row) => (
						<Box
							component='tr'
							key={row.lp}
							sx={{
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
								backgroundColor: row.highlight
									? 'background.subtle'
									: 'transparent',
								'&:hover': { backgroundColor: 'background.subtle' },
							}}
						>
							<Box
								component='td'
								sx={{
									padding: '5px 10px',
									fontWeight: 500,
								}}
							>
								{row.lp}
							</Box>
							<Box
								component='td'
								sx={{ padding: '5px 10px', color: 'text.secondary' }}
							>
								{row.type}
							</Box>
							<Box component='td' sx={{ padding: '5px 10px', fontWeight: 500 }}>
								{row.target}
							</Box>
							<Box component='td' sx={{ padding: '5px 10px' }}>
								<StatusPill
									label={row.pipelineStatus.label}
									color={row.pipelineStatus.color}
								/>
							</Box>
							<Box component='td' sx={{ padding: '5px 10px' }}>
								<StatusPill
									label={row.reupScore.label}
									color={row.reupScore.color}
									clickable
									onClick={() => onOpenDrawer(row.reupScore.drawerType, row.lp)}
								/>
							</Box>
							<Box component='td' sx={{ padding: '5px 10px' }}>
								<StatusPill
									label={row.vdrStatus.label}
									color={row.vdrStatus.color}
									clickable
								/>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>

		{/* Q&A */}
		<Box
			sx={{
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '12px',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					padding: '10px 14px',
					borderBottom: '0.5px solid',
					borderColor: 'border.light',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 1,
				}}
			>
				<Box>
					<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
						Open Q&A
					</Typography>
				</Box>
				<StatusPill label={'6 open • 3 overdue'} color='amber' />
				<Box
					component='button'
					sx={{
						fontSize: '10px',
						fontWeight: 600,
						color: 'vdr.text',
						background: 'vdr.bg',
						border: '0.5px solid',
						borderColor: 'vdr.border',
						borderRadius: '5px',
						padding: '1px 5px',
						cursor: 'pointer',
						fontFamily: 'inherit',
						'&:hover': { backgroundColor: 'vdr.hover' },
					}}
				>
					↔ Draft responses in Raise VDR Q&A tab
				</Box>
			</Box>
			{fund.qa.map((qa, i) => (
				<Box
					key={i}
					sx={{
						padding: '8px 14px',
						backgroundColor: qa.overdue
							? 'background.subtle'
							: 'background.paper',
						borderBottom: '0.5px solid',
						borderColor: 'border.light',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Box>
						<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
							{qa.lp} — {qa.topic}
						</Typography>
						<Typography
							sx={{
								marginTop: '4px',
								fontSize: '11px',
								color: 'text.secondary',
							}}
						>
							{qa.sub}
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							flexDirection: 'column',
							alignItems: 'flex-end',
						}}
					>
						<StatusPill label={qa.pill.label} color={qa.pill.color} />
						{qa.vdr ? (
							<Box
								component='button'
								sx={{
									fontSize: '10px',
									fontWeight: 600,
									color: 'vdr.text',
									background: 'vdr.bg',
									border: '0.5px solid',
									borderColor: 'vdr.border',
									borderRadius: '5px',
									padding: '1px 5px',
									cursor: 'pointer',
									fontFamily: 'inherit',
									marginLeft: 'auto',
									'&:hover': { backgroundColor: 'vdr.hover' },
								}}
							>
								↔ Open Raise VDR Q&A →
							</Box>
						) : null}
					</Box>
				</Box>
			))}
		</Box>
	</Box>
);

FundDetail.propTypes = {
	fund: PropTypes.object.isRequired,
	onOpenDrawer: PropTypes.func.isRequired,
};

// ── main component ──
const FundDashboard = ({
	showFundVDetail,
	setShowFundVDetail,
	onBack,
	onOpenDrawer,
}) => {
	const { fundDashboardKPIs, fundCards, activeFundDetail } = fakeData;

	return (
		<Box
			sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
		>
			<PageOverlayHeader
				title={showFundVDetail ? activeFundDetail.title : 'Fund Dashboard'}
				meta={showFundVDetail ? activeFundDetail.meta : '5 funds • $3.2B AUM'}
				onBack={onBack}
				badges={
					showFundVDetail ? (
						<Box sx={{ display: 'flex', gap: 1 }}>
							{activeFundDetail.badges.map((b) => (
								<StatusPill
									key={b.label}
									label={b.label}
									color={b.color}
									clickable={b.clickable}
								/>
							))}
						</Box>
					) : null
				}
				buttons={
					showFundVDetail ? (
						<Box
							component='button'
							sx={{
								fontSize: '10px',
								fontWeight: 600,
								color: 'vdr.text',
								background: 'vdr.bg',
								border: '0.5px solid',
								borderColor: 'vdr.border',
								borderRadius: '5px',
								padding: '1px 5px',
								cursor: 'pointer',
								fontFamily: 'inherit',
								'&:hover': { backgroundColor: 'vdr.hover' },
							}}
						>
							↔ Pipeline status live from Raise VDR
						</Box>
					) : null
				}
			/>
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					padding: '16px 20px',
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
				}}
			>
				{showFundVDetail ? (
					<FundDetail fund={activeFundDetail} onOpenDrawer={onOpenDrawer} />
				) : (
					<>
						{/* kpi strip */}
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(4, 1fr)',
								gap: '10px',
							}}
						>
							{fundDashboardKPIs.map((kpi) => (
								<KPICard key={kpi.label} {...kpi} />
							))}
						</Box>

						{/* fund cards */}
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(5, 1fr)',
								gap: '12px',
							}}
						>
							{fundCards.map((card) => (
								<FundCard
									key={card.name}
									card={card}
									onClickFundV={
										card.isFundV ? () => setShowFundVDetail(true) : undefined
									}
								/>
							))}
						</Box>
					</>
				)}
			</Box>
		</Box>
	);
};

FundDashboard.propTypes = {
	showFundVDetail: PropTypes.bool.isRequired,
	setShowFundVDetail: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onOpenDrawer: PropTypes.func.isRequired,
};

export default FundDashboard;
